import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import classNames from 'classnames';
import React from 'react';
import WhiteboardController from '../../controllers/WhiteboardController';
import { pluralize } from '@aha-app/aha-shared';
import { DurationUnit, shortFormattedTime } from '../../utils/time';
import { VotingSessionType } from '../../widgets/VotingSessionType';
import VotingSessionWidgetController from './controllers/VotingSessionWidgetController';
import FistToFiveVotingComponent from './FistToFiveVotingComponent';
import styles from './Widget.module.css';

const PokerInfo = ApplicationView(() => {
  const whiteboardController = useController(WhiteboardController);
  const votingSessionWidgetController = useController(
    VotingSessionWidgetController
  );

  if (votingSessionWidgetController.sidePanelIsOpen()) {
    return null;
  }

  return (
    <div className={classNames(styles.widget, styles.separated)}>
      <div className='flex-row align-items-center gap-4 px-3'>
        <span className='t-100'>Planning poker session is in progress</span>
        <aha-button
          kind='primary'
          onClick={() => whiteboardController.actionOpenSidePanel('voting')}
        >
          Start estimation
        </aha-button>
      </div>
    </div>
  );
});

const StandardAndFistToFiveInfo = ApplicationView(() => {
  const controller = useController(VotingSessionWidgetController);
  const { votesRemaining, isFinishedVoting } = controller;
  const { widget, remainingTime } = controller.state;

  if (!widget || controller.sidePanelIsOpen()) {
    return null;
  }
  if (
    widget.remainingDuration === Infinity &&
    votesRemaining === Infinity &&
    !isFinishedVoting
  ) {
    return null;
  }

  const isFistToFive = widget.sessionType === VotingSessionType.FistToFive;

  if (isFistToFive && widget.remainingDuration === Infinity) {
    return null;
  }

  const formattedTime = shortFormattedTime(remainingTime);
  return (
    <div className={classNames(styles.widget, styles.separated)}>
      {!isFinishedVoting && votesRemaining < Infinity && (
        <div className='flex-row align-items-center gap-2 px-3'>
          <span className='t-050'>{votesRemaining}</span>
          <span className='color-secondary'>
            {pluralize(votesRemaining, 'vote')} left
          </span>
        </div>
      )}
      {isFinishedVoting && !isFistToFive && (
        <div className={classNames('h-500', 'px-1', styles.noSeparator)}>
          <div className={classNames(styles.sqicon, styles.green)}>
            <strong>
              <i className='fa-regular fa-check'></i>
            </strong>
          </div>
        </div>
      )}
      {widget.remainingDuration > 0 && widget.remainingDuration < Infinity && (
        <div
          className={classNames(
            'h-500 flex-row align-items-center gap-2',
            styles.timer,
            {
              'text-warning': widget.inWarningTime,
              'text-error': widget.inDangerTime,
            }
          )}
        >
          <div className='flex-row align-items-center gap-2 px-3'>
            <i className='fa-regular fa-timer'></i>
            <span className='font-500'>
              {formattedTime.string}
              {formattedTime.unit === DurationUnit.Days
                ? ` ${formattedTime.shortUnit}`
                : ''}
            </span>
          </div>
        </div>
      )}
      {isFistToFive && <FistToFiveVotingComponent />}
      {!isFinishedVoting &&
        widget.sessionType === VotingSessionType.Standard && (
          <div className='flex-row align-items-center px-1'>
            <aha-button
              kind='ghost'
              class={styles.sqicon}
              onClick={() => controller.actionFinishVoting()}
            >
              <strong className='h-500'>
                <i className='fa-regular fa-check'></i>
              </strong>
            </aha-button>
            <aha-tooltip>Finish voting</aha-tooltip>
          </div>
        )}
    </div>
  );
});

const VotingSessionWidgetComponent: React.FC = () => {
  const controller = useController(VotingSessionWidgetController);
  const { widget } = controller.state;

  if (!widget) return null;
  if (widget.isFinished) return null;

  return widget.sessionType === VotingSessionType.Poker ? (
    <PokerInfo />
  ) : (
    <StandardAndFistToFiveInfo />
  );
};

const ControlledVotingSessionWidgetComponent = StartControllerScope(
  VotingSessionWidgetController,
  ApplicationView(VotingSessionWidgetComponent)
);

export default ControlledVotingSessionWidgetComponent;
